"use client"

import * as React from "react"
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react"
import { cn } from "@/lib/utils"

export interface Toast {
  id: string
  title?: string
  description?: string
  type?: "default" | "success" | "error" | "warning" | "info"
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

interface ToastContextType {
  toasts: Toast[]
  addToast: (toast: Omit<Toast, "id">) => void
  removeToast: (id: string) => void
}

const ToastContext = React.createContext<ToastContextType | undefined>(undefined)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([])

  const addToast = React.useCallback((toast: Omit<Toast, "id">) => {
    const id = Math.random().toString(36).substr(2, 9)
    const newToast = { ...toast, id }
    
    setToasts((prev) => [...prev, newToast])

    // Auto remove after duration
    const duration = toast.duration || 5000
    setTimeout(() => {
      removeToast(id)
    }, duration)
  }, [])

  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const context = React.useContext(ToastContext)
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider")
  }
  return context
}

function ToastContainer() {
  const { toasts, removeToast } = useToast()

  return (
    <div className="fixed bottom-0 right-0 z-50 p-4 space-y-4 pointer-events-none">
      {toasts.map((toast, index) => (
        <ToastItem
          key={toast.id}
          toast={toast}
          onRemove={() => removeToast(toast.id)}
          index={index}
        />
      ))}
    </div>
  )
}

interface ToastItemProps {
  toast: Toast
  onRemove: () => void
  index: number
}

function ToastItem({ toast, onRemove, index }: ToastItemProps) {
  const [isExiting, setIsExiting] = React.useState(false)
  
  const handleRemove = () => {
    setIsExiting(true)
    setTimeout(onRemove, 300)
  }

  const getIcon = () => {
    switch (toast.type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-teal-dark" />
      case "error":
        return <AlertCircle className="w-5 h-5 text-error-500" />
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-orange-cta" />
      case "info":
        return <Info className="w-5 h-5 text-light-blue-dark" />
      default:
        return null
    }
  }

  const getStyles = () => {
    switch (toast.type) {
      case "success":
        return "border-teal/30 bg-teal/20"
      case "error":
        return "border-error-500/30 bg-error-100"
      case "warning":
        return "border-orange-cta/30 bg-orange-cta/10"
      case "info":
        return "border-light-blue/30 bg-light-blue/20"
      default:
        return "border-off-black/10 bg-white"
    }
  }

  return (
    <div
      className={cn(
        "pointer-events-auto min-w-[350px] max-w-md rounded-lg border shadow-lg p-4",
        "transform transition-all duration-300 ease-out",
        getStyles(),
        isExiting 
          ? "animate-slide-out-right opacity-0" 
          : "animate-slide-in-right",
        "hover:scale-[1.02] hover:shadow-xl"
      )}
      style={{
        animationDelay: `${index * 50}ms`,
        animationFillMode: "both",
      }}
    >
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1 space-y-1">
          {toast.title && (
            <p className="text-sm font-semibold text-off-black">
              {toast.title}
            </p>
          )}
          {toast.description && (
            <p className="text-sm text-off-black/70">
              {toast.description}
            </p>
          )}
          {toast.action && (
            <button
              onClick={toast.action.onClick}
              className="text-sm font-medium text-teal-dark hover:text-teal transition-colors mt-2"
            >
              {toast.action.label}
            </button>
          )}
        </div>
        <button
          onClick={handleRemove}
          className="text-off-black/40 hover:text-off-black/70 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-beige/50 rounded-b-lg overflow-hidden">
        <div
          className="h-full bg-current opacity-30 animate-progress"
          style={{
            animationDuration: `${toast.duration || 5000}ms`,
          }}
        />
      </div>
    </div>
  )
}

// Add these animations to your globals.css
const toastAnimations = `
@keyframes slide-in-right {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slide-out-right {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

@keyframes progress {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}
`
